import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock,
  Copy,
  Lightbulb,
  Linkedin,
  ListChecks,
  MessagesSquare,
  Sparkles,
  Target,
  Twitter,
} from "lucide-react";
import MarketingPage from "../../components/marketing/MarketingPage";
import {
  breadcrumbSchema,
  faqSchema,
  ORGANIZATION_SCHEMA,
  SITE_URL,
  DEFAULT_OG_IMAGE,
} from "../../components/SEO";
import {
  getLeadGuide,
  LEAD_GUIDES,
  GUIDE_PUBLISHED,
  GUIDE_UPDATED,
} from "../../data/leadGuides";

// lucide-react has no Reddit brand glyph, so Reddit uses its generic
// threaded-conversation icon.
const PLATFORM_ICON = {
  twitter: Twitter,
  linkedin: Linkedin,
  reddit: MessagesSquare,
};

/**
 * Renders one of the pillar lead-generation guides from data.
 *
 * Both routes share this component so the heading hierarchy, anchor ids and
 * JSON-LD stay identical across the cluster — search engines and LLM crawlers
 * see one consistent template, and new platform guides are a data change.
 */
export default function LeadGuidePage({ guideKey }) {
  const guide = getLeadGuide(guideKey);
  // Every other guide in the cluster, in their canonical order — the hero
  // links the first one and "Keep reading" links them all, so adding a
  // platform guide stays a data change.
  const others = LEAD_GUIDES.filter((g) => g.key !== guide.key);
  const PlatformIcon = PLATFORM_ICON[guide.key] || Sparkles;
  const url = `${SITE_URL}${guide.path}`;

  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.seo.title,
    description: guide.seo.description,
    image: DEFAULT_OG_IMAGE,
    inLanguage: "en-US",
    datePublished: GUIDE_PUBLISHED,
    dateModified: GUIDE_UPDATED,
    author: ORGANIZATION_SCHEMA,
    publisher: ORGANIZATION_SCHEMA,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    about: {
      "@type": "Thing",
      name: `${guide.platform} lead generation`,
    },
    articleSection: "Lead generation",
    // Anchored sections give Google and answer engines a table of contents to
    // pull jump links and passage-level answers from.
    hasPart: guide.methods.map((m) => ({
      "@type": "WebPageElement",
      name: m.title,
      url: `${url}#${m.id}`,
    })),
  };

  const howTo = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `${guide.routine.heading} for finding leads on ${guide.platform}`,
    description: guide.routine.intro,
    totalTime: "PT30M",
    step: guide.routine.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      url: `${url}#daily-routine`,
    })),
  };

  const breadcrumbs = breadcrumbSchema([
    { name: "Home", path: "/" },
    { name: "Lead generation", path: "/lead-generation" },
    { name: `${guide.platform} lead generation`, path: guide.path },
  ]);

  const toc = [
    { id: "short-answer", label: "The short answer" },
    { id: "why-platform", label: `Why ${guide.shortName} works` },
    { id: "buying-signals", label: "The 8 buying signals" },
    { id: "search-strings", label: "Search strings that work" },
    ...guide.methods.map((m, i) => ({ id: m.id, label: `${i + 1}. ${m.title}` })),
    { id: "daily-routine", label: "The 30-minute routine" },
    { id: "mistakes", label: "Mistakes to avoid" },
    { id: "with-collavue", label: "Manual vs Collavue" },
    { id: "faq", label: "FAQ" },
  ];

  return (
    <MarketingPage
      seo={{
        title: guide.seo.title,
        description: guide.seo.description,
        path: guide.path,
        keywords: guide.seo.keywords,
        type: "article",
        section: "Lead generation",
        publishedTime: GUIDE_PUBLISHED,
        modifiedTime: GUIDE_UPDATED,
        imageAlt: `Collavue guide: how to find leads on ${guide.platform}`,
        schema: [article, howTo, faqSchema(guide.faqs), breadcrumbs],
      }}
    >
      <article>
        {/* Hero */}
        <header className="relative overflow-hidden">
          <div
            className="absolute inset-0 -z-10 opacity-60"
            style={{
              backgroundImage:
                "radial-gradient(1200px 400px at 80% -20%, rgba(45,91,255,0.10), transparent 60%)",
            }}
          />
          <div className="max-w-7xl mx-auto px-6 pt-12 pb-14 lg:pt-16">
            <Breadcrumbs
              trail={[
                { name: "Home", to: "/" },
                { name: "Lead generation", to: "/lead-generation" },
                { name: guide.platform },
              ]}
            />

            <p className="mt-6 inline-flex items-center gap-2 text-[#2d5bff] font-medium text-sm tracking-wide uppercase">
              <PlatformIcon className="w-4 h-4" aria-hidden="true" />
              {guide.hero.eyebrow}
            </p>
            <h1 className="font-display text-[#0a1736] text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] mt-3 max-w-4xl">
              {guide.hero.h1}{" "}
              <span className="italic text-[#2d5bff]">{guide.hero.h1Italic}</span>
            </h1>
            <p className="mt-6 text-lg text-[#5a6478] max-w-3xl leading-relaxed">
              {guide.hero.intro}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#5a6478]">
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" aria-hidden="true" />
                12 min read
              </span>
              <span>
                Updated{" "}
                <time dateTime={GUIDE_UPDATED}>
                  {formatDate(GUIDE_UPDATED)}
                </time>
              </span>
              <span>By the Collavue team</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="px-6 py-3 rounded-md bg-[#0a1736] text-white font-medium hover:bg-[#1c2a4d] inline-flex items-center gap-2 transition-colors"
              >
                Find your first leads free <ArrowRight className="w-4 h-4" />
              </Link>
              {others.map((g) => (
                <Link
                  key={g.key}
                  to={g.path}
                  className="px-6 py-3 rounded-md bg-white text-[#0a1736] border border-[#e3dccb] font-medium hover:border-[#0a1736] transition-colors"
                >
                  {g.navLabel}
                </Link>
              ))}
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-6 pb-4 lg:grid lg:grid-cols-12 lg:gap-10">
          {/* Table of contents */}
          <aside className="lg:col-span-3 lg:order-2">
            <nav
              aria-label="On this page"
              className="lg:sticky lg:top-24 rounded-xl border border-[#e3dccb] bg-white p-5 mb-10 lg:mb-0"
            >
              <h2 className="text-xs uppercase tracking-wider text-[#5a6478] font-medium">
                On this page
              </h2>
              <ol className="mt-3 space-y-1.5 text-sm max-h-[60vh] overflow-y-auto pr-1">
                {toc.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="text-[#1c2a4d] hover:text-[#2d5bff] transition-colors block leading-snug"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>

          <div className="lg:col-span-9 lg:order-1">
            {/* Answer-first block */}
            <section id="short-answer" aria-labelledby="short-answer-heading" className="scroll-mt-24">
              <div className="rounded-2xl border border-[#2d5bff]/25 bg-[#2d5bff]/[0.04] p-6 md:p-8">
                <h2
                  id="short-answer-heading"
                  className="font-display text-2xl md:text-3xl text-[#0a1736] font-semibold"
                >
                  {guide.answer.heading}
                </h2>
                <p className="mt-4 text-[#1c2a4d] leading-relaxed">
                  {guide.answer.body}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {guide.answer.bullets.map((b) => (
                    <li key={b} className="flex gap-3 text-[#1c2a4d]">
                      <CheckCircle2
                        className="w-5 h-5 text-[#2d5bff] shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Why this platform */}
            <Section
              id="why-platform"
              title={`Why ${guide.platform} works for finding leads`}
            >
              <div className="grid sm:grid-cols-2 gap-4">
                {guide.quickFacts.map((f) => (
                  <div
                    key={f.title}
                    className="rounded-xl border border-[#e3dccb] bg-white p-5"
                  >
                    <h3 className="font-display text-lg text-[#0a1736] font-semibold">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#5a6478] leading-relaxed">
                      {f.body}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Buying signals */}
            <Section
              id="buying-signals"
              title={`The 8 buying signals worth acting on`}
              intro={guide.signalsIntro}
            >
              <div className="overflow-x-auto rounded-xl border border-[#e3dccb] bg-white">
                <table className="w-full text-left text-sm min-w-[640px]">
                  <thead className="bg-[#0a1736] text-white">
                    <tr>
                      <th scope="col" className="px-5 py-3 font-medium">Signal</th>
                      <th scope="col" className="px-5 py-3 font-medium">What it looks like</th>
                      <th scope="col" className="px-5 py-3 font-medium">Why it converts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guide.signals.map((s) => (
                      <tr key={s.signal} className="border-t border-[#e3dccb] align-top">
                        <th
                          scope="row"
                          className="px-5 py-4 text-[#0a1736] font-semibold text-left"
                        >
                          {s.signal}
                        </th>
                        <td className="px-5 py-4 text-[#5a6478] italic">{s.example}</td>
                        <td className="px-5 py-4 text-[#5a6478]">{s.why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Search strings */}
            <Section
              id="search-strings"
              title={guide.cheatsheet.heading}
              intro={guide.cheatsheet.intro}
            >
              <div className="space-y-3">
                {guide.cheatsheet.rows.map((row) => (
                  <QueryCard key={row.query} query={row.query} finds={row.finds} />
                ))}
              </div>
              <div className="mt-6 rounded-xl border border-[#e3dccb] bg-white p-5">
                <h3 className="font-display text-lg text-[#0a1736] font-semibold inline-flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-[#2d5bff]" aria-hidden="true" />
                  Operator notes
                </h3>
                <ul className="mt-3 space-y-2 text-sm text-[#5a6478]">
                  {guide.cheatsheet.notes.map((n) => (
                    <li key={n} className="flex gap-2.5">
                      <ChevronRight
                        className="w-4 h-4 text-[#2d5bff] shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <span className="leading-relaxed">{n}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Section>

            {/* Methods */}
            <Section
              id="methods"
              title={`9 ways to find leads on ${guide.platform}`}
              intro={guide.methodsIntro}
            >
              <div className="space-y-8">
                {guide.methods.map((m, i) => (
                  <section
                    key={m.id}
                    id={m.id}
                    className="scroll-mt-24 rounded-2xl border border-[#e3dccb] bg-white p-6 md:p-8"
                    aria-labelledby={`${m.id}-heading`}
                  >
                    <div className="flex items-start gap-4">
                      <span
                        className="shrink-0 w-9 h-9 rounded-lg bg-[#0a1736] text-white font-display font-semibold flex items-center justify-center"
                        aria-hidden="true"
                      >
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <h3
                          id={`${m.id}-heading`}
                          className="font-display text-2xl text-[#0a1736] font-semibold leading-snug"
                        >
                          {m.title}
                        </h3>
                        <p className="mt-3 text-[#5a6478] leading-relaxed">
                          {m.summary}
                        </p>

                        <h4 className="mt-6 text-sm uppercase tracking-wider text-[#5a6478] font-medium inline-flex items-center gap-2">
                          <ListChecks className="w-4 h-4 text-[#2d5bff]" aria-hidden="true" />
                          How to run it
                        </h4>
                        <ol className="mt-3 space-y-2.5 list-decimal list-outside pl-5 marker:text-[#2d5bff] marker:font-semibold">
                          {m.steps.map((s) => (
                            <li key={s} className="text-[#1c2a4d] leading-relaxed pl-1">
                              {s}
                            </li>
                          ))}
                        </ol>

                        <div className="mt-5 rounded-lg bg-[#0a1736] px-4 py-3 overflow-x-auto">
                          <p className="text-[10px] uppercase tracking-wider text-white/50">
                            Example
                          </p>
                          <code className="block mt-1 text-sm text-[#bef264] font-mono whitespace-pre-wrap break-words">
                            {m.example}
                          </code>
                        </div>

                        <p className="mt-4 text-sm text-[#5a6478] border-l-2 border-[#2d5bff] pl-4 leading-relaxed">
                          <span className="font-semibold text-[#0a1736]">Pro tip: </span>
                          {m.tip}
                        </p>
                      </div>
                    </div>
                  </section>
                ))}
              </div>
            </Section>

            {/* Daily routine */}
            <Section
              id="daily-routine"
              title={guide.routine.heading}
              intro={guide.routine.intro}
            >
              <ol className="space-y-3">
                {guide.routine.steps.map((s, i) => (
                  <li
                    key={s.name}
                    className="flex gap-4 rounded-xl border border-[#e3dccb] bg-white p-5"
                  >
                    <span
                      className="shrink-0 w-8 h-8 rounded-full bg-[#2d5bff]/10 text-[#2d5bff] font-semibold flex items-center justify-center text-sm"
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <div>
                      <h3 className="font-semibold text-[#0a1736]">{s.name}</h3>
                      <p className="mt-1 text-sm text-[#5a6478] leading-relaxed">
                        {s.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>

            {/* Mistakes */}
            <Section id="mistakes" title="Six mistakes that kill your reply rate">
              <div className="grid sm:grid-cols-2 gap-4">
                {guide.mistakes.map((m) => (
                  <div
                    key={m.title}
                    className="rounded-xl border border-[#e3dccb] bg-white p-5"
                  >
                    <h3 className="font-semibold text-[#0a1736] inline-flex items-start gap-2">
                      <AlertTriangle
                        className="w-4 h-4 text-[#f46a4e] shrink-0 mt-1"
                        aria-hidden="true"
                      />
                      <span>{m.title}</span>
                    </h3>
                    <p className="mt-2 text-sm text-[#5a6478] leading-relaxed">
                      {m.body}
                    </p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Manual vs Collavue */}
            <Section id="with-collavue" title={guide.comparison.heading}>
              <div className="overflow-x-auto rounded-xl border border-[#e3dccb] bg-white">
                <table className="w-full text-left text-sm min-w-[560px]">
                  <thead className="bg-[#0a1736] text-white">
                    <tr>
                      <th scope="col" className="px-5 py-3 font-medium">Step</th>
                      <th scope="col" className="px-5 py-3 font-medium">By hand</th>
                      <th scope="col" className="px-5 py-3 font-medium">With Collavue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guide.comparison.rows.map((row) => (
                      <tr key={row[0]} className="border-t border-[#e3dccb]">
                        <th scope="row" className="px-5 py-3 text-[#0a1736] font-medium text-left">
                          {row[0]}
                        </th>
                        <td className="px-5 py-3 text-[#5a6478]">{row[1]}</td>
                        <td className="px-5 py-3 text-[#2d5bff] font-medium">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* FAQ */}
            <Section
              id="faq"
              title={`Finding leads on ${guide.platform}: FAQ`}
            >
              <div className="space-y-3">
                {guide.faqs.map((f) => (
                  <details
                    key={f.q}
                    className="group rounded-xl border border-[#e3dccb] bg-white p-5 open:border-[#2d5bff]/40"
                  >
                    <summary className="font-semibold text-[#0a1736] cursor-pointer list-none flex items-start justify-between gap-4">
                      <h3 className="text-base font-semibold">{f.q}</h3>
                      <ChevronRight
                        className="w-5 h-5 text-[#2d5bff] shrink-0 transition-transform group-open:rotate-90"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="mt-3 text-[#5a6478] leading-relaxed">{f.a}</p>
                  </details>
                ))}
              </div>
            </Section>
          </div>
        </div>

        {/* Cross-links */}
        <section
          className="bg-white border-t border-[#e3dccb]"
          aria-labelledby="keep-reading-heading"
        >
          <div className="max-w-7xl mx-auto px-6 py-16">
            <h2
              id="keep-reading-heading"
              className="font-display text-3xl text-[#0a1736] font-semibold"
            >
              Keep reading
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((g) => (
                <RelatedCard
                  key={g.key}
                  to={g.path}
                  icon={PLATFORM_ICON[g.key]}
                  title={`How to find leads on ${g.platform}`}
                  body={g.seo.description}
                />
              ))}
              <RelatedCard
                to="/lead-generation"
                icon={Target}
                title="X vs LinkedIn vs Reddit for lead generation"
                body="Which channel to start with, how they differ, and the fundamentals that apply to all three."
              />
              <RelatedCard
                to="/reply-agent"
                icon={Sparkles}
                title="The Reply Agent"
                body="Drafts on-brand replies to the leads you find — with human approval before anything sends."
              />
              <RelatedCard
                to="/lead-inbox"
                icon={ListChecks}
                title="The Lead Inbox"
                body="Every scored X, LinkedIn and Reddit lead in one filterable feed instead of twelve browser tabs."
              />
              <RelatedCard
                to="/alternatives"
                icon={Check}
                title="Compare the alternatives"
                body="Feature-by-feature breakdowns against the social listening tools you have already evaluated."
              />
              <RelatedCard
                to="/pricing"
                icon={ArrowRight}
                title="Pricing"
                body="Credit packs that scale with how many conversations you actually work."
              />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="bg-[#0a1736] text-white"
          aria-labelledby="guide-cta-heading"
        >
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <PlatformIcon className="w-8 h-8 text-[#bef264] mx-auto" aria-hidden="true" />
            <h2
              id="guide-cta-heading"
              className="font-display text-3xl md:text-4xl font-semibold mt-4 leading-tight"
            >
              Run this playbook without the 30 minutes a day.
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
              Collavue runs these searches continuously across {guide.platform},
              scores every match on intent and ICP fit, and drafts the reply in
              your brand voice. You approve what goes out — nothing sends on its own.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/signup"
                className="px-6 py-3 rounded-md bg-[#bef264] text-[#0a1736] font-semibold hover:bg-[#a3e635] transition-colors"
              >
                Start free
              </Link>
              <Link
                to="/product"
                className="px-6 py-3 rounded-md border border-white/20 text-white font-medium hover:border-white/60 transition-colors"
              >
                See how it works
              </Link>
            </div>
          </div>
        </section>
      </article>
    </MarketingPage>
  );
}

function Section({ id, title, intro, children }) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-heading`}
      className="scroll-mt-24 mt-14"
    >
      <h2
        id={`${id}-heading`}
        className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold leading-tight"
      >
        {title}
      </h2>
      {intro && (
        <p className="mt-4 text-[#5a6478] leading-relaxed max-w-3xl">{intro}</p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

export function Breadcrumbs({ trail }) {
  return (
    <nav aria-label="Breadcrumb" className="text-sm text-[#5a6478]">
      <ol className="flex flex-wrap items-center gap-1">
        {trail.map((item, i) => (
          <li key={item.name} className="flex items-center gap-1">
            {i > 0 && <span aria-hidden="true" className="mx-1">/</span>}
            {item.to ? (
              <Link to={item.to} className="hover:text-[#2d5bff] transition-colors">
                {item.name}
              </Link>
            ) : (
              <span className="text-[#0a1736]" aria-current="page">
                {item.name}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function QueryCard({ query, finds }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(query);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard is unavailable in some browsers and insecure contexts —
      // the query is still selectable, so fail quietly rather than alerting.
    }
  };

  return (
    <div className="rounded-xl border border-[#e3dccb] bg-white overflow-hidden">
      <div className="flex items-start gap-3 bg-[#0a1736] px-4 py-3">
        <code className="flex-1 min-w-0 text-sm text-[#bef264] font-mono whitespace-pre-wrap break-words">
          {query}
        </code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-white/20 px-2.5 py-1 text-xs text-white/80 hover:text-white hover:border-white/50 transition-colors"
          aria-label={copied ? "Copied search string" : "Copy search string"}
        >
          {copied ? (
            <Check className="w-3.5 h-3.5" aria-hidden="true" />
          ) : (
            <Copy className="w-3.5 h-3.5" aria-hidden="true" />
          )}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="px-4 py-3 text-sm text-[#5a6478] leading-relaxed">{finds}</p>
    </div>
  );
}

function RelatedCard({ to, icon: Icon, title, body }) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-[#e3dccb] bg-[#faf6ee]/50 p-5 hover:border-[#2d5bff] hover:shadow-[0_10px_40px_-25px_rgba(45,91,255,0.45)] transition-all"
    >
      {Icon && <Icon className="w-5 h-5 text-[#2d5bff]" aria-hidden="true" />}
      <h3 className="font-display text-lg text-[#0a1736] font-semibold mt-3 leading-snug">
        {title}
      </h3>
      <p className="mt-2 text-sm text-[#5a6478] leading-relaxed line-clamp-3">
        {body}
      </p>
    </Link>
  );
}

function formatDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}
